import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';

const SimpleWebhookTester = () => {
  // Stati per gestire i campi del form
  const [url, setUrl] = useState('');
  const [event, setEvent] = useState('maintenance.created');
  const [payload, setPayload] = useState(JSON.stringify({ id: 123, description: "Test rapido" }, null, 2));
  
  // Stati per gestire la risposta
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  // Lista degli eventi disponibili
  const eventOptions = [
    'maintenance.created',
    'maintenance.completed',
    'maintenance.status_changed',
    'sale.created',
    'sale.status_changed',
    'order.created',
    'order.updated',
    'product.created',
    'product.updated',
    'user.login',
    'system.error'
  ];

  // Funzione per validare il payload JSON
  const validateJson = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Funzione per inviare il test webhook
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazioni
    if (!url) {
      setError('Inserire un URL valido');
      return;
    }
    
    if (!validateJson(payload)) {
      setError('Il payload non è un JSON valido');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const response = await fetch('/api/webhooks/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url,
          event,
          payload: JSON.parse(payload)
        })
      });
      
      const data = await response.json();
      setResponse(data);
    } catch (err) {
      setError(`Errore durante l'invio: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Test Rapido Webhook
      </Typography>
      
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="URL Webhook"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://esempio.com/webhook"
            margin="normal"
            required
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Evento</InputLabel>
            <Select
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              label="Evento"
            >
              {eventOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Payload JSON"
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            margin="normal"
            multiline
            rows={4}
            required
            error={payload && !validateJson(payload)}
            helperText={payload && !validateJson(payload) ? 'JSON non valido' : ''}
          />
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              {loading ? 'Invio...' : 'Invia Test'}
            </Button>
          </Box>
        </form>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {response && (
        <Alert 
          severity={response.success ? "success" : "warning"} 
          sx={{ mt: 2 }}
        >
          <Typography variant="subtitle2">
            {response.success 
              ? `✅ Webhook inviato con successo: ${response.status} ${response.statusText}` 
              : `⚠️ Risposta con stato: ${response.status} ${response.statusText}`}
          </Typography>
          
          {response.message && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {response.message}
            </Typography>
          )}
          
          {response.data && (
            <Box 
              component="pre" 
              sx={{ 
                mt: 1, 
                p: 1, 
                bgcolor: 'background.paper', 
                borderRadius: 1,
                fontSize: '0.875rem',
                overflow: 'auto'
              }}
            >
              {JSON.stringify(response.data, null, 2)}
            </Box>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default SimpleWebhookTester; 